import { Controller } from "@hotwired/stimulus";

// Mémoire du scroll de la nav de parcours.
//
// À chaque « Suivant », `_turbo_step_advance.turbo_stream.erb` remplace la frame
// `steps_recap_*` en entier : le conteneur scrollable est un nœud neuf, donc son
// scrollTop repart à 0 et la nav remonte en haut. On garde donc la position dans un
// module (il survit au remplacement, contrairement au contrôleur) et on la repose au
// connect suivant.
//
// Cas particulier demandé : si l'utilisateur était déjà en bas, on recolle en bas
// plutôt qu'à l'ancien offset — l'étape qui vient d'apparaître reste ainsi visible.
const MEMORY = new Map();

// Marge de tolérance pour « on est en bas » (arrondis sub-pixel, ombre de fin de liste).
const BOTTOM_THRESHOLD = 24;

// Fenêtre pendant laquelle on re-pose la position : les lignes d'étapes (badges, frames
// lazy) peuvent grandir juste après le connect et décaler ce qu'on vient de restaurer.
const SETTLE_MS = 600;

export default class extends Controller {
  connect() {
    this.boundSave = () => this.save();
    // Les événements `scroll` ne bouillonnent pas : on écoute en capture pour attraper
    // aussi bien cet élément que le conteneur scrollable interne.
    this.element.addEventListener("scroll", this.boundSave, { passive: true, capture: true });

    this.restore();
  }

  disconnect() {
    this.element.removeEventListener("scroll", this.boundSave, { capture: true });
    this.stopSettling();
    this.save();
  }

  get key() {
    return this.element.closest("turbo-frame")?.id || "workflow-step-nav";
  }

  // Le scroller est soit l'élément lui-même, soit le wrapper interne (`nav_html_content`)
  // selon la largeur / le mode replié : on le résout à chaque fois plutôt qu'au connect.
  scroller() {
    if (this.isScrollable(this.element)) return this.element;

    return Array.from(this.element.querySelectorAll("*")).find((node) => this.isScrollable(node)) || null;
  }

  isScrollable(node) {
    if (!node || node.scrollHeight - node.clientHeight <= 1) return false;

    const { overflowY } = getComputedStyle(node);
    return /(auto|scroll|overlay)/.test(overflowY);
  }

  save() {
    if (this.suppress) return;
    // Un scroll venu de l'utilisateur annule la restauration en cours : sinon on lui
    // reprendrait la main pendant la fenêtre de stabilisation.
    this.applying = false;

    const scroller = this.scroller();
    if (!scroller) return;

    const max = scroller.scrollHeight - scroller.clientHeight;
    // Nav courte ou squelette de chargement : rien à mémoriser, et surtout ne pas écraser
    // la position retenue avant le remplacement.
    if (max <= 1) return;

    MEMORY.set(this.key, {
      scrollTop: scroller.scrollTop,
      atBottom: max - scroller.scrollTop <= BOTTOM_THRESHOLD,
    });
  }

  restore() {
    const state = MEMORY.get(this.key);
    if (!state) return;

    this.applying = true;
    const apply = () => this.applyState(state);

    apply();
    requestAnimationFrame(apply);

    if (typeof ResizeObserver === "function") {
      this.observer = new ResizeObserver(() => {
        if (this.applying) apply();
      });
      this.observer.observe(this.element);
      const scroller = this.scroller();
      if (scroller && scroller !== this.element) this.observer.observe(scroller);
    }

    this.settleTimer = setTimeout(() => this.stopSettling(), SETTLE_MS);
  }

  applyState(state) {
    if (!this.applying) return;

    const scroller = this.scroller();
    if (!scroller) return;

    const max = scroller.scrollHeight - scroller.clientHeight;
    if (max <= 0) return;

    const target = state.atBottom ? max : Math.min(state.scrollTop, max);
    if (Math.abs(scroller.scrollTop - target) < 1) return;

    // Le scroll qu'on provoque déclenche `save()` : on le marque pour ne pas mémoriser
    // une position intermédiaire (contenu pas encore à sa taille finale).
    this.suppress = true;
    scroller.scrollTop = target;
    requestAnimationFrame(() => {
      this.suppress = false;
    });
  }

  stopSettling() {
    this.applying = false;
    clearTimeout(this.settleTimer);
    this.observer?.disconnect();
    this.observer = null;
  }
}
